import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Mail, Settings, Check, X, ExternalLink, Key,
  RefreshCw, Send, AlertCircle, Eye, EyeOff,
  CheckCircle, Clock, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import AppHeader from '@/components/shared/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailConfiguration = ({ user, onLogout }) => {
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('setup');
  
  // Setup form
  const [apiKey, setApiKey] = useState('');
  const [fromName, setFromName] = useState('Nirvaana Wellness');
  const [fromEmail, setFromEmail] = useState('noreply@nirvaana.com');
  const [showKey, setShowKey] = useState(false);
  const [connecting, setConnecting] = useState(false);
  
  // Test email
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [configRes, logsRes] = await Promise.all([
        axios.get(`${API}/email/config`, { headers }),
        axios.get(`${API}/email/logs?limit=20`, { headers })
      ]);
      
      setConfig(configRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!apiKey) {
      toast.error('Please enter your Resend API key');
      return;
    }
    
    setConnecting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/email/configure`, {
        api_key: apiKey,
        from_name: fromName,
        from_email: fromEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Email service connected successfully!');
      setApiKey('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect email service');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect email service? Marketing emails will stop working.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/email/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Email service disconnected');
      setConfig({ is_configured: false });
    } catch (error) {
      toast.error('Failed to disconnect email service');
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/email/test?to_email=${encodeURIComponent(testEmail)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.sent) {
        toast.success('Test email sent successfully!');
        fetchData(); // Refresh logs
      } else {
        toast.error(response.data.error || 'Failed to send test email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader user={user} onLogout={onLogout} showBack={true} backTo="/admin/settings" />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onLogout={onLogout} showBack={true} backTo="/admin/settings" />
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif text-foreground">Email Configuration</h1>
              <p className="text-muted-foreground">Configure Resend for marketing campaigns & notifications</p>
            </div>
          </div>
          
          {config?.is_configured && (
            <Badge variant="default" className="bg-blue-600">
              <CheckCircle className="w-3 h-3 mr-1" /> Connected
            </Badge>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="setup" data-testid="email-setup-tab">
              <Settings className="w-4 h-4 mr-2" /> Setup
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="email-logs-tab">
              <Clock className="w-4 h-4 mr-2" /> Email Logs
            </TabsTrigger>
          </TabsList>

          {/* Setup Tab */}
          <TabsContent value="setup" className="space-y-6">
            {/* Info Card */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-blue-800 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Resend Email Service
                </CardTitle>
                <CardDescription className="text-blue-700">
                  Connect your Resend account to send marketing emails, booking confirmations, and notifications.
                  <br />
                  <strong>Note:</strong> You pay Resend directly - free tier includes 3,000 emails/month!
                </CardDescription>
              </CardHeader>
              <CardContent>
                <a 
                  href="https://resend.com/api-keys" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  Get your Resend API key (free signup)
                </a>
              </CardContent>
            </Card>

            {config?.is_configured ? (
              /* Connected State */
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <CheckCircle className="w-5 h-5" />
                    Email Service Connected
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">From Name</Label>
                      <p className="font-medium">{config.from_name}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">From Email</Label>
                      <p className="font-mono text-sm">{config.from_email}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Connected Since</Label>
                      <p className="text-sm">{formatDate(config.connected_at)}</p>
                    </div>
                  </div>
                  
                  {/* Test Email */}
                  <div className="p-4 bg-muted rounded-lg">
                    <Label className="mb-2 block">Send Test Email</Label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="Enter email address"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        data-testid="test-email-input"
                      />
                      <Button 
                        onClick={handleSendTest}
                        disabled={sendingTest}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="send-test-email-btn"
                      >
                        {sendingTest ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Test</>}
                      </Button>
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    onClick={handleDisconnect}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid="disconnect-email-btn"
                  >
                    <X className="w-4 h-4 mr-2" /> Disconnect Email Service
                  </Button>
                </CardContent>
              </Card>
            ) : (
              /* Setup Form */
              <Card>
                <CardHeader>
                  <CardTitle>Connect Resend Email Service</CardTitle>
                  <CardDescription>
                    Enter your API key from resend.com
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>API Key <span className="text-red-500">*</span></Label>
                    <div className="flex gap-2">
                      <Input
                        type={showKey ? "text" : "password"}
                        placeholder="re_xxxxxxxxxxxx"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        data-testid="resend-api-key-input"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setShowKey(!showKey)}
                      >
                        {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">resend.com/api-keys</a>
                    </p>
                  </div>
                  
                  <div>
                    <Label>From Name</Label>
                    <Input
                      placeholder="Nirvaana Wellness"
                      value={fromName}
                      onChange={(e) => setFromName(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label>From Email</Label>
                    <Input
                      type="email"
                      placeholder="noreply@yourdomain.com"
                      value={fromEmail}
                      onChange={(e) => setFromEmail(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Must be a verified domain in your Resend account
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleConnect}
                    disabled={connecting}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="connect-email-btn"
                  >
                    {connecting ? (
                      <>Connecting...</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" /> Connect Email Service</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Use Cases Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What you can send with Email</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="font-medium text-blue-800">Marketing Campaigns</p>
                    <p className="text-sm text-blue-600">Send targeted emails to VIP, Loyal, or At-Risk customers</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="font-medium text-green-800">Booking Confirmations</p>
                    <p className="text-sm text-green-600">Automatic emails when bookings are made</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="font-medium text-purple-800">Hotel Staff Onboarding</p>
                    <p className="text-sm text-purple-600">Welcome emails for new hotel portal users</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="font-medium text-amber-800">Feedback Requests</p>
                    <p className="text-sm text-amber-600">Post-service feedback collection emails</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                  FAQ: Why Configure Your Own Email API?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <details className="group border border-slate-200 rounded-lg">
                  <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-foreground hover:bg-slate-50 rounded-lg">
                    <span>Why can't WellNest send emails for me?</span>
                    <span className="transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="p-4 pt-0 text-muted-foreground text-sm">
                    <p>WellNest sends your <strong>credential emails</strong> (staff logins, property onboarding) using our platform email.</p>
                    <p className="mt-2">However, <strong>marketing emails</strong> (feedback requests, campaigns, targeted offers) should come from <strong>your brand</strong> to build customer trust and avoid being marked as spam.</p>
                  </div>
                </details>

                <details className="group border border-slate-200 rounded-lg">
                  <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-foreground hover:bg-slate-50 rounded-lg">
                    <span>What is Resend and how much does it cost?</span>
                    <span className="transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="p-4 pt-0 text-muted-foreground text-sm">
                    <p><strong>Resend</strong> is a modern email sending service used by thousands of businesses worldwide.</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li><strong>Free tier:</strong> 3,000 emails/month (perfect for small spas)</li>
                      <li><strong>Pro tier:</strong> $20/month for 50,000 emails</li>
                      <li>You pay Resend directly - no middleman fees!</li>
                    </ul>
                    <p className="mt-2"><a href="https://resend.com/pricing" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">View Resend Pricing →</a></p>
                  </div>
                </details>

                <details className="group border border-slate-200 rounded-lg">
                  <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-foreground hover:bg-slate-50 rounded-lg">
                    <span>How do I get a Resend API key?</span>
                    <span className="transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="p-4 pt-0 text-muted-foreground text-sm">
                    <ol className="list-decimal list-inside space-y-2">
                      <li>Go to <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">resend.com</a> and sign up (free)</li>
                      <li>Verify your email address</li>
                      <li>Go to <strong>API Keys</strong> → <strong>Create API Key</strong></li>
                      <li>Copy the key and paste it here</li>
                      <li>(Optional) Add your domain for better deliverability</li>
                    </ol>
                    <p className="mt-3 text-xs bg-amber-50 p-2 rounded">
                      <strong>Tip:</strong> Start with Resend's free tier. You can upgrade anytime as your business grows!
                    </p>
                  </div>
                </details>

                <details className="group border border-slate-200 rounded-lg">
                  <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-foreground hover:bg-slate-50 rounded-lg">
                    <span>What happens after I connect my Email API?</span>
                    <span className="transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="p-4 pt-0 text-muted-foreground text-sm">
                    <p>Once connected, WellNest will automatically:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Send <strong>feedback requests</strong> after every completed service</li>
                      <li>Include your <strong>Google Review link</strong> in feedback emails</li>
                      <li>Enable <strong>targeted marketing</strong> from the Customer Intelligence dashboard</li>
                      <li>Allow you to send <strong>campaigns</strong> to VIP, Loyal, or At-Risk customers</li>
                    </ul>
                    <p className="mt-2">All emails will come from <strong>your brand name</strong> and <strong>your email address</strong>!</p>
                  </div>
                </details>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Recent Emails</CardTitle>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {logs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No emails sent yet
                  </p>
                ) : (
                  <div className="space-y-2">
                    {logs.map((log) => (
                      <div key={log.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          {log.status === 'sent' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{log.to_email}</p>
                            <p className="text-xs text-muted-foreground">
                              {log.subject}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={log.status === 'sent' ? 'default' : 'destructive'}>
                            {log.status}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(log.sent_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EmailConfiguration;
